import React, { memo, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  Computer,
  People,
  Description,
  WarningAmber,
} from '@mui/icons-material';
import { THEME_COLORS, BREAKPOINTS } from '../../constants';

/**
 * Componente de estadísticas de dispositivos
 */
const DeviceStats = memo(({ stats }) => {
  const isMobile = useMediaQuery(`(max-width:${BREAKPOINTS.MOBILE}px)`);

  // Memoizar datos de estadísticas (completamente minimalista con animaciones hermosas)
  const statsData = useMemo(() => [
    {
      title: 'Total Dispositivos',
      value: stats?.total || 0,
      description: 'dispositivos registrados',
      icon: <Computer sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
    },
    {
      title: 'Con Contactos',
      value: stats?.with_contacts || 0,
      description: 'asociados a contactos',
      icon: <People sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
    },
    {
      title: 'Con Templates',
      value: stats?.with_templates || 0,
      description: 'con plantillas activas',
      icon: <Description sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
    },
    {
      title: 'Sin Configurar',
      value: stats?.without_config || 0,
      description: 'sin configuración',
      icon: <WarningAmber sx={{ fontSize: '2rem', color: THEME_COLORS.TEXT_PRIMARY }} />,
    },
  ], [stats]);

  return (
    <Grid container spacing={3} sx={{ marginBottom: 4 }}>
      {statsData.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card
            sx={{
              height: '100%',
              border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              borderRadius: '12px',
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
              transition: 'all 0.3s ease-in-out',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)',
                borderColor: THEME_COLORS.TEXT_PRIMARY,
              },
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: THEME_COLORS.TEXT_PRIMARY,
                      fontWeight: 700,
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      marginBottom: 0.5,
                      lineHeight: 1,
                    }}
                  >
                    {stat.value.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: THEME_COLORS.TEXT_SECONDARY,
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      marginBottom: 0.25,
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: THEME_COLORS.TEXT_MUTED,
                      fontSize: '0.75rem',
                      textTransform: 'lowercase',
                    }}
                  >
                    {stat.description}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: '12px',
                    backgroundColor: `${THEME_COLORS.TEXT_PRIMARY}10`,
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

DeviceStats.displayName = 'DeviceStats';

export default DeviceStats; 